import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, Edit3, Save, X, Users } from 'lucide-react';
import './TeamManagement.css';

function TeamManagement({ token, nightMode, onBack, embedded = false }) {
  const [teams, setTeams] = useState([]);
  const [newTeam, setNewTeam] = useState({ name: '', color: '#007bff' });
  const [editingTeam, setEditingTeam] = useState(null);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [newPlayer, setNewPlayer] = useState({ name: '', position: '', number: '' });
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [showAddPlayer, setShowAddPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rosterSearch, setRosterSearch] = useState('');

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(response.data);
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch teams');
      setLoading(false);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!newTeam.name.trim()) {
      setError('Team name is required');
      return;
    }

    // Check for duplicate team names
    if (teams.some(team => team.name.toLowerCase() === newTeam.name.trim().toLowerCase())) {
      setError('A team with this name already exists');
      return;
    }

    try {
      setError('');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/teams`,
        { ...newTeam, name: newTeam.name.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTeams([...teams, response.data]);
      setNewTeam({ name: '', color: '#007bff' });
      setShowAddTeam(false);
      setSuccess('Team created successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to create team: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleUpdateTeam = async (teamId, updatedData) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/teams/${teamId}`,
        updatedData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTeams(teams.map(team => team._id === teamId ? response.data : team));
      setEditingTeam(null);
    } catch (error) {
      setError('Failed to update team: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team? This will also delete all associated matches.')) {
      return;
    }

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/teams/${teamId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(teams.filter(team => team._id !== teamId));
    } catch (error) {
      setError('Failed to delete team');
    }
  };

  const handleAddPlayer = async (teamId) => {
    if (!newPlayer.name.trim()) {
      setError('Player name is required');
      return;
    }

    // Check for duplicate player names in the same team
    const team = teams.find(t => t._id === teamId);
    if (team && team.players.some(player => 
      player.name.toLowerCase() === newPlayer.name.trim().toLowerCase()
    )) {
      setError('A player with this name already exists in this team');
      return;
    }

    // Check for duplicate jersey numbers in the same team
    if (newPlayer.number && team && team.players.some(player => 
      player.number && player.number.toString() === newPlayer.number.toString()
    )) {
      setError('A player with this jersey number already exists in this team');
      return;
    }

    try {
      setError('');
      const playerData = {
        ...newPlayer,
        name: newPlayer.name.trim(),
        position: newPlayer.position.trim(),
        number: newPlayer.number ? parseInt(newPlayer.number) : undefined
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/teams/${teamId}/players`,
        playerData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTeams(teams.map(team => team._id === teamId ? response.data : team));
      setNewPlayer({ name: '', position: '', number: '' });
      setShowAddPlayer(null);
      setSuccess('Player added successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to add player: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleUpdatePlayer = async (teamId, playerId, updatedPlayer) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/teams/${teamId}/players/${playerId}`,
        updatedPlayer,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTeams(teams.map(team => team._id === teamId ? response.data : team));
      setEditingPlayer(null);
    } catch (error) {
      setError('Failed to update player');
    }
  };

  const handleDeletePlayer = async (teamId, playerId) => {
    if (!window.confirm('Are you sure you want to remove this player?')) {
      return;
    }

    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/teams/${teamId}/players/${playerId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTeams(teams.map(team => team._id === teamId ? response.data : team));
    } catch (error) {
      setError('Failed to delete player');
    }
  };

  if (loading) {
    return (
      <div className={`team-management ${nightMode ? 'night-mode' : ''}`}>
        <div className="loading">Loading teams...</div>
      </div>
    );
  }

  return (
    <div className={`team-management ${nightMode ? 'night-mode' : ''} ${embedded ? 'embedded' : ''}`}>
      <div className="team-management-header">
        {!embedded && (
          <button onClick={onBack} className="back-btn">
            ← Back to Scoreboard
          </button>
        )}
        {!embedded && <h1>Teams</h1>}
        <button 
          onClick={() => setShowAddTeam(true)} 
          className="add-team-btn"
        >
          <Plus size={18} /> Add Team
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {teams.length > 0 && (
        <input
          className="roster-search"
          type="search"
          placeholder="Filter rosters…"
          value={rosterSearch}
          onChange={(e) => setRosterSearch(e.target.value)}
        />
      )}

      {showAddTeam && (
        <div className="add-team-form">
          <h3>Add New Team</h3>
          <form onSubmit={handleCreateTeam}>
            <div className="form-group">
              <input
                type="text"
                placeholder="Team Name"
                value={newTeam.name}
                onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                required
              />
              <input
                type="color"
                value={newTeam.color}
                onChange={(e) => setNewTeam({ ...newTeam, color: e.target.value })}
                title="Team Color"
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="save-btn">
                <Save size={16} /> Save
              </button>
              <button 
                type="button" 
                onClick={() => setShowAddTeam(false)} 
                className="cancel-btn"
              >
                <X size={16} /> Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="teams-grid">
        {teams.filter((team) => team.name.toLowerCase().includes(rosterSearch.trim().toLowerCase())).map(team => (
          <div key={team._id} className="team-card">
            <div className="team-header" style={{ borderLeftColor: team.color }}>
              {editingTeam === team._id ? (
                <div className="edit-team-form">
                  <input
                    type="text"
                    value={team.name}
                    onChange={(e) => {
                      const updatedTeams = teams.map(t => 
                        t._id === team._id ? { ...t, name: e.target.value } : t
                      );
                      setTeams(updatedTeams);
                    }}
                  />
                  <input
                    type="color"
                    value={team.color}
                    onChange={(e) => {
                      const updatedTeams = teams.map(t => 
                        t._id === team._id ? { ...t, color: e.target.value } : t
                      );
                      setTeams(updatedTeams);
                    }}
                  />
                  <div className="edit-actions">
                    <button 
                      onClick={() => handleUpdateTeam(team._id, { name: team.name, color: team.color })}
                      className="save-btn-small"
                    >
                      <Save size={14} />
                    </button>
                    <button 
                      onClick={() => setEditingTeam(null)}
                      className="cancel-btn-small"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <h3>{team.name}</h3>
                  <div className="team-actions">
                    <button 
                      onClick={() => setEditingTeam(team._id)}
                      className="edit-btn"
                    >
                      <Edit3 size={16} />
                    </button>
                    <button 
                      onClick={() => handleDeleteTeam(team._id)}
                      className="delete-btn"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="players-section">
              <div className="players-header">
                <h4>Players ({team.players?.length || 0})</h4>
                <button 
                  onClick={() => setShowAddPlayer(team._id)}
                  className="add-player-btn"
                >
                  <Plus size={14} /> Add Player
                </button>
              </div>

              {showAddPlayer === team._id && (
                <div className="add-player-form">
                  <input
                    type="text"
                    placeholder="Player Name"
                    value={newPlayer.name}
                    onChange={(e) => setNewPlayer({ ...newPlayer, name: e.target.value })}
                  />
                  <input
                    type="text"
                    placeholder="Position"
                    value={newPlayer.position}
                    onChange={(e) => setNewPlayer({ ...newPlayer, position: e.target.value })}
                  />
                  <input
                    type="number"
                    placeholder="Number"
                    value={newPlayer.number}
                    onChange={(e) => setNewPlayer({ ...newPlayer, number: e.target.value })}
                  />
                  <div className="player-form-actions">
                    <button onClick={() => handleAddPlayer(team._id)} className="save-btn-small">
                      <Save size={14} />
                    </button>
                    <button onClick={() => setShowAddPlayer(null)} className="cancel-btn-small">
                      <X size={14} />
                    </button>
                  </div>
                </div>
              )}

              <div className="players-list">
                {team.players?.map(player => (
                  <div key={player._id} className="player-item">
                    {editingPlayer === player._id ? (
                      <div className="edit-player-form">
                        <input
                          type="text"
                          value={player.name}
                          onChange={(e) => {
                            const updatedTeams = teams.map(t => 
                              t._id === team._id ? {
                                ...t,
                                players: t.players.map(p => 
                                  p._id === player._id ? { ...p, name: e.target.value } : p
                                )
                              } : t
                            );
                            setTeams(updatedTeams);
                          }}
                        />
                        <input
                          type="text"
                          value={player.position}
                          onChange={(e) => {
                            const updatedTeams = teams.map(t => 
                              t._id === team._id ? {
                                ...t,
                                players: t.players.map(p => 
                                  p._id === player._id ? { ...p, position: e.target.value } : p
                                )
                              } : t
                            );
                            setTeams(updatedTeams);
                          }}
                        />
                        <input
                          type="number"
                          value={player.number}
                          onChange={(e) => {
                            const updatedTeams = teams.map(t => 
                              t._id === team._id ? {
                                ...t,
                                players: t.players.map(p => 
                                  p._id === player._id ? { ...p, number: e.target.value } : p
                                )
                              } : t
                            );
                            setTeams(updatedTeams);
                          }}
                        />
                        <div className="player-edit-actions">
                          <button 
                            onClick={() => handleUpdatePlayer(team._id, player._id, player)}
                            className="save-btn-small"
                          >
                            <Save size={12} />
                          </button>
                          <button 
                            onClick={() => setEditingPlayer(null)}
                            className="cancel-btn-small"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="player-info">
                          <span className="player-name">{player.name}</span>
                          {player.position && <span className="player-position">({player.position})</span>}
                          {player.number && <span className="player-number">#{player.number}</span>}
                        </div>
                        <div className="player-actions">
                          <button 
                            onClick={() => setEditingPlayer(player._id)}
                            className="edit-btn-small"
                          >
                            <Edit3 size={12} />
                          </button>
                          <button 
                            onClick={() => handleDeletePlayer(team._id, player._id)}
                            className="delete-btn-small"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {teams.length === 0 && (
        <div className="empty-state">
          <Users size={48} />
          <p>No teams created yet. Add your first team to get started!</p>
        </div>
      )}
    </div>
  );
}

export default TeamManagement;
